import React from "react";
import Link from "next/link";
import Image from "next/image";
import { unescape } from "lodash";
import PropTypes from "prop-types";
import Clock from "components/icons/clock";
import styled, { css } from "styled-components";
import Location from "components/icons/location";
import ServiceTypePills from "components/ServiceTypePills";

const OuterCardContainer = styled.li`
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  list-style-type: none;
  margin-right: 1.125rem;
  width: 18.375rem;

  :last-child {
    margin-right: 0;
  }

  ${({ theme }) => theme.mq.md`
    width: 19.125rem;
  `}

  ${({ theme }) => theme.mq.xl`
    width: 18.375rem;
  `}
`;

const InnerCardContainer = styled.div`
  display: flex;
  flex-direction: ${({ sideBySide }) => {
    return sideBySide ? "row" : "column";
  }};
`;

const BizImage = styled.div`
  border: 0.1875rem solid transparent;
  border-radius: 0.5rem;
  height: 12.375rem;
  margin-bottom: 0.75rem;
  overflow: hidden;
  position: relative;
  width: 18.375rem;

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.primary};
  }

  ${({ theme }) => theme.mq.md`
    height: 12.875rem;
    width: 19.125rem;
  `}

  ${({ theme }) => theme.mq.xl`
    height: 12rem;
    width: 18rem;
  `}
`;

const BizAttribute = styled.p`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSize(1)};
  font-weight: 400;
  line-height: 1.125rem;
  max-height: 1.125rem;
  margin-bottom: 0.375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ isSearchResult }) =>
    !isSearchResult &&
    css`
      white-space: nowrap;
    `}

  svg {
    margin-bottom: 0.1875rem;
  }

  .address {
    display: inline;
    font-weight: 400;
  }
`;

const BizName = styled.h4`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  font-size: ${({ theme }) => theme.fontSize(4)};
  letter-spacing: -0.025rem;
  line-height: 1.8125rem;
  margin-bottom: 0.25rem;
  word-break: break-word;
`;

const ClockIcon = styled(Clock)`
  height: 0.9375rem;
  margin-right: 0.3125rem;
  width: 0.9375rem;
`;

const LocationIcon = styled(Location)`
  height: 0.9375rem;
  margin-right: 0.1875rem;
  width: 0.9375rem;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 0.1875rem;

  ${({ sideBySide }) =>
    sideBySide &&
    css`
      padding: 0 1.25rem;
      max-width: 36.25rem;
      width: 100%;
    `}
`;

function FeaturedBusinessCardComponent({ business, businessImage, className, isSearchResult, sideBySide }) {
  const {
    attributes,
    include: { serviceTypes, vendorBusinessOpenHours },
  } = business;
  const { businessName, friendlyName, urlLocality, urlRegion } = attributes;
  const types = serviceTypes.data;
  const hours = vendorBusinessOpenHours.data;
  let open;
  let close;
  let openMinutes;
  let closeMinutes;
  let openHours;
  let closeHours;
  if (hours) {
    open = new Date(hours[0].attributes.calcStartTime);
    close = new Date(hours[0].attributes.calcEndTime);
    openMinutes = open.getMinutes();
    closeMinutes = close.getMinutes();
    openHours = open.getHours();
    closeHours = close.getHours();
  }
  const formattedAddress = business?.attributes?.formattedAddress;
  return (
    <OuterCardContainer as={isSearchResult ? "div" : null} className={className} isSearchResult={isSearchResult} sideBySide={sideBySide}>
      <Link href={`/business/${urlRegion}/${urlLocality}/${friendlyName}`}>
        <a>
          <InnerCardContainer sideBySide={sideBySide}>
            <BizImage sideBySide={sideBySide} isSearchResult={isSearchResult}>
              <Image layout="fill" objectFit="cover" objectPosition="center" loading="lazy" src={businessImage} alt={businessName} />
            </BizImage>
            <DescriptionWrapper sideBySide={sideBySide}>
              <ServiceTypePills serviceTypes={types} businessName={businessName} />
              <BizName>{unescape(businessName)}</BizName>
              {formattedAddress && (
                <BizAttribute isSearchResult={isSearchResult}>
                  {" "}
                  <LocationIcon />
                  <address className="address">{formattedAddress}</address>
                </BizAttribute>
              )}
              <BizAttribute isSearchResult={isSearchResult}>
                {hours ? <ClockIcon /> : null}
                {hours
                  ? `${openHours >= 12 ? openHours - 12 : openHours}:${`0${openMinutes}`.slice(-2)} ${openHours >= 12 ? "PM" : "AM"} - ${
                      closeHours >= 12 ? closeHours - 12 : closeHours
                    }:${`0${closeMinutes}`.slice(-2)} ${closeHours >= 12 ? "PM" : "AM"}`
                  : null}
              </BizAttribute>
            </DescriptionWrapper>
          </InnerCardContainer>
        </a>
      </Link>
    </OuterCardContainer>
  );
}

export default FeaturedBusinessCardComponent;

FeaturedBusinessCardComponent.propTypes = {
  businessImage: PropTypes.string.isRequired,
  business: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.object,
    include: PropTypes.shape({
      serviceTypes: PropTypes.shape({
        data: PropTypes.array,
      }),
      vendorBusinessOpenHours: PropTypes.shape({
        data: PropTypes.oneOfType(
          [
            PropTypes.arrayOf(
              PropTypes.shape({
                attributes: PropTypes.shape({
                  calcStartTime: PropTypes.string,
                  calcEndTime: PropTypes.string,
                }),
              })
            ),
          ],
          PropTypes.shape({
            attributes: PropTypes.shape({
              calcStartTime: PropTypes.string,
              calcEndTime: PropTypes.string,
            }),
          })
        ),
      }),
    }),
  }).isRequired,
  className: PropTypes.string,
  isSearchResult: PropTypes.bool,
  sideBySide: PropTypes.bool,
};

FeaturedBusinessCardComponent.defaultProps = {
  className: "",
  isSearchResult: false,
  sideBySide: false,
};
